import {
  bridge,
  BridgeError,
  type InitResult,
  type UserInfo,
} from '@kherel/app-bridge';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { CAPABILITIES } from './bridge-protocol.js';
import { initialBridgeConnection } from './connection.js';
import { formatResult } from './redact.js';

type ConnectionState = 'connecting' | 'connected' | 'standalone' | 'error';
type LogStatus = 'running' | 'success' | 'error';

interface CallLog {
  id: string;
  label: string;
  output: string;
  status: LogStatus;
  time: string;
}

function errorMessage(error: unknown): string {
  if (error instanceof BridgeError) {
    return `${error.code}: ${error.message}`;
  }
  if (error instanceof Error) return error.message;
  return String(error);
}

export function TesterApp() {
  const [connection, setConnection] =
    useState<ConnectionState>('connecting');
  const [initResult, setInitResult] = useState<InitResult | null>(null);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [logs, setLogs] = useState<CallLog[]>([]);
  const [searchQuery, setSearchQuery] = useState('Anton');
  const [scopes, setScopes] = useState('profile:read');
  const [userId, setUserId] = useState('');
  const [message, setMessage] = useState('Hello from Tribe SDK Lab');
  const [backendPath, setBackendPath] = useState('/v0/apps/app-wrapper/ping');
  const startedRef = useRef(false);
  const isMock = useMemo(
    () => new URLSearchParams(window.location.search).get('lab_mode') === 'mock',
    [],
  );

  const run = useCallback(
    async <T,>(label: string, operation: () => Promise<T>): Promise<T | null> => {
      const id = crypto.randomUUID();
      const time = new Date().toLocaleTimeString();
      const runningEntry: CallLog = {
        id,
        label,
        output: 'Waiting for host response…',
        status: 'running',
        time,
      };

      setLogs((current) =>
        [runningEntry, ...current].slice(0, 30),
      );

      try {
        const result = await operation();
        setLogs((current) =>
          current.map((entry) =>
            entry.id === id
              ? {
                  ...entry,
                  output: formatResult(result),
                  status: 'success',
                }
              : entry,
          ),
        );
        return result;
      } catch (error) {
        setLogs((current) =>
          current.map((entry) =>
            entry.id === id
              ? {
                  ...entry,
                  output: errorMessage(error),
                  status: 'error',
                }
              : entry,
          ),
        );
        return null;
      }
    },
    [],
  );

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    if (!initialBridgeConnection) {
      setConnection('standalone');
      return;
    }

    const connectionPromise = initialBridgeConnection;
    void (async () => {
      const initialized = await run(
        'Initialize bridge',
        () => connectionPromise,
      );

      if (!initialized) {
        setConnection('error');
        return;
      }

      setInitResult(initialized);
      setConnection('connected');

      const viewer = await run('Get user information', () =>
        bridge.getUserInfo(),
      );
      if (viewer) {
        setUser(viewer);
        setUserId(viewer.id);
      }
    })();
  }, [run]);

  const connected = connection === 'connected';
  const modeLabel = isMock ? 'Mock host' : 'Live Tribe host';

  return (
    <main className="tester">
      <header className="tester-header">
        <div>
          <div className="brand-row">
            <div className="brand-mark brand-mark--small" aria-hidden="true">
              ↔
            </div>
            <div>
              <p className="eyebrow">App Bridge 0.8.0</p>
              <h1>SDK capability console</h1>
            </div>
          </div>
          <p className="lede">
            Exercise every public Tribe application capability through the same
            postMessage path used by production mini apps.
          </p>
        </div>

        <div className={`connection connection--${connection}`}>
          <span aria-hidden="true" />
          <div>
            <strong>
              {connection === 'connected'
                ? 'Connected'
                : connection === 'connecting'
                  ? 'Connecting'
                  : 'Not connected'}
            </strong>
            <small>{modeLabel}</small>
          </div>
        </div>
      </header>

      <section className="summary-grid">
        <article>
          <span>Host version</span>
          <strong>{initResult?.tribeHostVersion ?? '—'}</strong>
        </article>
        <article>
          <span>App ID</span>
          <strong>{initResult?.tribeAppId ?? '—'}</strong>
        </article>
        <article>
          <span>Viewer</span>
          <strong>
            {user?.isAnonymous ? 'Anonymous' : user?.name || '—'}
          </strong>
        </article>
        <article>
          <span>RFT badge</span>
          <strong>{user?.rftBadge ?? 'None'}</strong>
        </article>
      </section>

      {connection === 'standalone' && (
        <div className="notice notice--warning">
          Open <a href="/">the standalone harness</a> or launch this route
          through App Wrapper. The SDK needs a parent host.
        </div>
      )}

      <div className="tester-layout">
        <section className="capability-column">
          <div className="section-title">
            <div>
              <p className="eyebrow">Live controls</p>
              <h2>16 bridge capabilities</h2>
            </div>
            <span>{CAPABILITIES.length}/16 mapped</span>
          </div>

          <div className="capability-groups">
            <CapabilityGroup title="Session & identity">
              <Action
                disabled={!connected}
                label="Initialize"
                onClick={() => run('Initialize bridge', () => bridge.init())}
              />
              <Action
                disabled={!connected}
                label="Launch parameters"
                onClick={() =>
                  run('Get launch parameters', () => bridge.getLaunchParams())
                }
              />
              <Action
                disabled={!connected}
                label="User information"
                onClick={() =>
                  run('Get user information', () => bridge.getUserInfo())
                }
              />
            </CapabilityGroup>

            <CapabilityGroup title="Discovery & permissions">
              <Field label="Search query">
                <input
                  onChange={(event) => setSearchQuery(event.target.value)}
                  value={searchQuery}
                />
              </Field>
              <Action
                disabled={!connected || !searchQuery.trim()}
                label="Search users"
                onClick={() =>
                  run('Search users', () =>
                    bridge.searchUsers({ query: searchQuery, limit: 5 }),
                  )
                }
              />
              <Field label="Scopes (comma separated)">
                <input
                  onChange={(event) => setScopes(event.target.value)}
                  value={scopes}
                />
              </Field>
              <Action
                disabled={!connected || !scopes.trim()}
                label="Request scopes"
                onClick={() =>
                  run('Request scopes', () =>
                    bridge.requestScopes({
                      scopes: scopes
                        .split(',')
                        .map((scope) => scope.trim())
                        .filter(Boolean),
                    }),
                  )
                }
              />
              <Field label="Backend path">
                <input
                  onChange={(event) => setBackendPath(event.target.value)}
                  value={backendPath}
                />
              </Field>
              <Action
                disabled={!connected || !backendPath.startsWith('/v0/apps/')}
                label="Call host backend"
                onClick={() =>
                  run('Call host backend', () =>
                    bridge.callBackend({
                      method: 'GET',
                      path: backendPath,
                    }),
                  )
                }
              />
            </CapabilityGroup>

            <CapabilityGroup title="Navigation & host UI">
              <Field label="Tribe user ID">
                <input
                  onChange={(event) => setUserId(event.target.value)}
                  value={userId}
                />
              </Field>
              <div className="action-pair">
                <Action
                  disabled={!connected || !userId}
                  label="Open profile"
                  onClick={() =>
                    run('Open profile', () => bridge.openProfile({ userId }))
                  }
                />
                <Action
                  disabled={!connected || !userId}
                  label="Open DM"
                  onClick={() =>
                    run('Open direct message', () =>
                      bridge.openDirectMessage({ userId, message }),
                    )
                  }
                />
              </div>
              <Field label="Prefilled message">
                <input
                  onChange={(event) => setMessage(event.target.value)}
                  value={message}
                />
              </Field>
              <div className="action-pair">
                <Action
                  disabled={!connected}
                  label="Show alert"
                  onClick={() =>
                    run('Show host alert', () =>
                      bridge.showAlert({
                        title: 'SDK Lab',
                        message: 'The host-native alert channel works.',
                      }),
                    )
                  }
                />
                <Action
                  disabled={!connected}
                  label="Show confirm"
                  onClick={() =>
                    run('Show host confirmation', () =>
                      bridge.showConfirm({
                        title: 'SDK Lab',
                        message: 'Confirm the bridge response path.',
                      }),
                    )
                  }
                />
              </div>
              <div className="action-pair">
                <Action
                  disabled={!connected}
                  label="Resize"
                  onClick={() =>
                    run('Resize iframe', () =>
                      bridge.resize({
                        height: Math.ceil(
                          document.documentElement.scrollHeight,
                        ),
                      }),
                    )
                  }
                />
                <Action
                  disabled={!connected}
                  label="Open link"
                  onClick={() =>
                    run('Open external link', () =>
                      bridge.openLink({
                        url: 'https://wearereal.com/de/apps',
                        target: '_blank',
                      }),
                    )
                  }
                />
              </div>
              <Action
                danger
                disabled={!connected}
                label="Close application"
                onClick={() =>
                  run('Close application', () =>
                    bridge.close('sdk-lab-test'),
                  )
                }
              />
            </CapabilityGroup>

            <CapabilityGroup title="Billing & analytics">
              <Action
                disabled={!connected}
                label="Get billing"
                onClick={() =>
                  run('Get billing snapshot', () => bridge.getBilling())
                }
              />
              <Action
                disabled={!connected}
                label="Track test event"
                onClick={() =>
                  run('Track analytics event', () =>
                    bridge.trackEvent('sdk_lab_test', {
                      mode: isMock ? 'mock' : 'live',
                    }),
                  )
                }
              />
              <div className="action-pair">
                <Action
                  danger
                  disabled={!connected}
                  label="Buy subscription"
                  onClick={() =>
                    run('Request subscription purchase', () =>
                      bridge.requestPurchase('subscription'),
                    )
                  }
                />
                <Action
                  danger
                  disabled={!connected}
                  label="Buy lifetime"
                  onClick={() =>
                    run('Request lifetime purchase', () =>
                      bridge.requestPurchase('lifetime'),
                    )
                  }
                />
              </div>
            </CapabilityGroup>
          </div>
        </section>

        <aside className="result-column">
          <div className="section-title section-title--sticky">
            <div>
              <p className="eyebrow">Response stream</p>
              <h2>Bridge log</h2>
            </div>
            <button
              className="text-button"
              disabled={logs.length === 0}
              onClick={() => setLogs([])}
              type="button"
            >
              Clear
            </button>
          </div>

          <div className="log-list">
            {logs.length === 0 ? (
              <div className="empty-log">
                <span aria-hidden="true">⌁</span>
                <p>Run a capability to inspect its sanitized response.</p>
              </div>
            ) : (
              logs.map((entry) => (
                <article className={`log-entry log-entry--${entry.status}`} key={entry.id}>
                  <header>
                    <span>{entry.label}</span>
                    <time>{entry.time}</time>
                  </header>
                  <pre>{entry.output}</pre>
                </article>
              ))
            )}
          </div>
        </aside>
      </div>
    </main>
  );
}

function CapabilityGroup({
  children,
  title,
}: {
  children: ReactNode;
  title: string;
}) {
  return (
    <section className="capability-group">
      <h3>{title}</h3>
      <div>{children}</div>
    </section>
  );
}

function Field({
  children,
  label,
}: {
  children: ReactNode;
  label: string;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
    </label>
  );
}

function Action({
  danger = false,
  disabled,
  label,
  onClick,
}: {
  danger?: boolean;
  disabled: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      className={danger ? 'action action--danger' : 'action'}
      disabled={disabled}
      onClick={onClick}
      type="button"
    >
      <span>{label}</span>
      <span aria-hidden="true">↗</span>
    </button>
  );
}
